'use strict';

const Mn = require('backbone.marionette');
const i18n = require('i18next');
const CanvasView = require('balus/ui/view/body/project/content/index');
const ModeIndicatorView = require('common/ui/view/mode-indicator');
const SidebarView = require('common/ui/view/sidebar/index');
const SidebarCollection = require('common/ui/view/sidebar/collection');
const CommentCollectionRepository = require('communication/domain/repository/comment-collection');
const RelatedKeys = require('common/domain/value/related-keys');
const ActionRepository = require('process/domain/repository/action/index');
const TimelineView = require('balus/ui/view/timeline/index');
const TaskCollectionRepository = require('communication/domain/repository/task-collection');
const TaskListView = require('communication/ui/view/task/related-list');
const PrimaryContextView = require('balus/ui/view/primary-context');
const AdditionalView = require('balus/ui/view/additional/index');
const MemberView = require('balus/ui/view/body/project/sidebar/member');
const ContentTemplate = require('balus/ui/template/body/project/index.hbs');
const CoverView = require('balus/ui/view/body/project/cover');
const ToolbarView = require('balus/ui/view/body/project/toolbar/index');
const NavView = require('balus/ui/view/body/project/nav/index');

module.exports = Mn.View.extend({
    className: 'row',
    template: ContentTemplate,
    regions: {
        cover: '#cover',
        nav: '#nav',
        toolbar: '#toolbar',
        content: '#content',
        sidebar: '#sidebar',
        modeIndicator: '#mode-indicator'
    },
    childViewEvents: {
        'close:sidebar': 'closeSidebar',
        'enter:full:screen': 'enterFullScreen',
        'exit:full:screen': 'exitFullScreen',
        'gantt:edit': 'startGanttEditMode',
        'gantt:done': 'endGanttEditMode',
        'change:model': 'render'
    },

    onRender: function() {
        const context = this.getOption('context');
        const user = context.getUser();
        const group = context.getGroup();

        this.showChildView('cover', new CoverView({
            model: this.model,
            context: this.getOption('context'),
            project: true
        }));
        this.showChildView('nav', new NavView({
            model: this.model,
            context: context
        }));
        this.showChildView('content', new CanvasView({
            model: this.model,
            context: this.getOption('context')
        }));
        this.showChildView('toolbar', new ToolbarView({
            model: this.model,
            user: user,
            group: group,
            context: context
        }));

        const actionId = this.model.getActionID();
        let action;
        ActionRepository.find(this.model).get(actionId)
            .then(a => {
                action = a;
                return Promise.all([
                    CommentCollectionRepository.find(this.model).byKey(this.model.key).fetchFirstPage(),
                    TaskCollectionRepository.find(this.model).byKey(this.model.key).fetchFirstPage(10)
                ]);
            })
            .then(values => {
                const comments = values[0];
                const tasks = values[1];
                const relatedKeys = new RelatedKeys([]);
                const navs = new SidebarCollection(
                    [
                        {
                            name: 'timeline',
                            icon: 'fa fa-comments-o',
                            label: 'module/word:timeline',
                            view: new TimelineView({
                                model: this.model,
                                collection: comments,
                                context: this.getOption('context'),
                                onCommentActivate: () => {
                                    this.getChildView('sidebar').activate('timeline');
                                },
                            }),
                            badge: {
                                content: function(){
                                    return comments.getTotalCount();
                                },
                                listen: function(onChange){
                                    comments.on('change:totalCount', onChange);
                                }
                            },
                            open: true
                        },
                        {
                            name: 'task',
                            icon: 'fa fa-check-square',
                            label: 'module/word:task',
                            view: new TaskListView({
                                collection: tasks,
                                context: this.getOption('context'),
                                PrimaryContextView: PrimaryContextView,
                                AdditionalView: AdditionalView,
                                relatedKeys: relatedKeys
                            }),
                            badge: {
                                content: function(){
                                    return tasks.getTotalCount();
                                },
                                hasUnread: function() {
                                    return tasks.hasUnfinishedTask();
                                },
                                listen: function(onChange){
                                    tasks.on('change:totalCount', onChange);
                                    tasks.on('change:update', onChange);
                                }
                            },
                        },
                        {
                            name: 'member',
                            icon: 'fa fa-users',
                            label: 'module/word:projectMember',
                            view: new MemberView({
                                model: this.model,
                                collection: this.model.getMembers({withSuspended: true}),
                                user: user,
                                group: group
                            })
                        }
                    ]
                );

                this.showChildView('sidebar', new SidebarView({
                    navs: navs,
                    horizontalNav: true
                }));

                this.getChildView('toolbar').renderNav(navs);

                const activateComment = () => {
                    const commentTimelineView = this.getChildView('sidebar').getView('timeline');
                    commentTimelineView.activateByHashState();
                };
                activateComment();
                window.addEventListener('hashchange', activateComment, false);
                this.on('destroy', () => {
                    window.removeEventListener('hashchange', activateComment, false);
                });

                const taskListView = navs.getByName('task').getView();
                taskListView.on('reply', (comment, task) => {
                    const timeline = navs.getByName('timeline');
                    timeline.activate();
                    timeline.getView().onReply(comment);
                    timeline.getView().onReplyTask(task);
                });

                const activateTask = () => {
                    const pattern = new RegExp('#/tasks/([0-9a-f]+)');
                    const taskID = location.hash && location.hash.match(pattern) && location.hash.match(pattern)[1];
                    if(taskID) {
                        const task = tasks.get(taskID);
                        if(task) {
                            this.getChildView('sidebar').activate('task');
                            task.activate();
                        }
                    }
                };

                activateTask();
                window.addEventListener('hashchange', activateTask, false);
            });
    },

    enterFullScreen: function() {
        this.getChildView('cover').closeNav();
        this.closeSidebar();
    },

    exitFullScreen: function() {
        this.getChildView('cover').openNav();
        this.getChildView('sidebar').open();
    },

    startGanttEditMode: function() {
        this.showChildView('modeIndicator', new ModeIndicatorView({
            title: i18n.t('balus_template_body_project:indicator.edit-gantt')
        }));
        this.getChildView('content').startGanttEditMode();
    },

    endGanttEditMode: function() {
        this.getRegion('modeIndicator').empty();
        this.getChildView('content').endGanttEditMode();
    },

    closeSidebar: function() {
        this.getChildView('sidebar').close();
    }
});



// WEBPACK FOOTER //
// ./balus/ui/view/body/project/index.js